/**
 * Core-100 R4 — CORE-AU (OD-22): the lift state of a smart-assign ranking reaches the ASSIGNMENT surface.
 *
 * Constitution CORE-AU: "Where the vehicle's specification OR the equipment's rating is unknown, the system
 * MUST NOT infer that the pairing is safe." smart-assign annotates a technician whose lift(s) cannot be shown
 * safe for this vehicle with `liftVerificationRequired` (+ basis and reason), and a technician whose every lift
 * is KNOWN to be too small with `liftSafetyBlock`. Before this component neither field was rendered anywhere,
 * so such a technician looked plainly compatible. The ranking keeps its place (post-filter law) — this notice
 * labels the pairing; the physical gate (apply_vehicle_location_locked) still refuses an unknown vehicle weight on
 * a RATED lift until a manager verifies it (OD-22).
 *
 * OD-33 (founder decision, revised 2026-10-15): a lift with NO rated capacity on record no longer refuses — the
 * gate allows the placement and marks it "Capacity Not Verified". So that basis is shown as the capacity warning
 * (allowed, never presented as safe), not as a block.
 */
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { CAPACITY_NOT_VERIFIED_TITLE } from '@/lib/location/liftCapacityNotice';

export interface RankingLiftFacts {
  liftSafetyBlock?: boolean | null;
  liftSafetyReason?: string | null;
  liftVerificationRequired?: boolean | null;
  liftVerificationBasis?: 'lift_rating_unknown' | 'vehicle_weight_unknown' | string | null;
  liftVerificationReason?: string | null;
}

export const LIFT_VERIFICATION_LABEL = 'Lift verification required';
export const LIFT_UNSAFE_LABEL = 'No lift rated for this vehicle';

const BASIS_TEXT: Record<string, string> = {
  lift_rating_unknown: 'the lift has no rated capacity on record',
  vehicle_weight_unknown: "the vehicle's weight is unknown",
};

/** True when the ranking must NOT be presented as plainly compatible. */
export function rankingLiftNeedsAttention(r: RankingLiftFacts | null | undefined): boolean {
  return !!r && (r.liftSafetyBlock === true || r.liftVerificationRequired === true);
}

export function RankingLiftNotice({ ranking, technicianId }: { ranking: RankingLiftFacts | null | undefined; technicianId: string }) {
  if (!ranking) return null;
  if (ranking.liftSafetyBlock === true) {
    return (
      <div
        className="rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive space-y-1"
        data-testid={`ranking-lift-${technicianId}`}
        data-lift-state="unsafe"
        role="alert"
      >
        <p className="flex items-center gap-1 font-medium">
          <ShieldAlert className="h-3.5 w-3.5" aria-hidden /> {LIFT_UNSAFE_LABEL}
        </p>
        {ranking.liftSafetyReason && <p>{ranking.liftSafetyReason}</p>}
      </div>
    );
  }
  if (ranking.liftVerificationRequired === true && ranking.liftVerificationBasis === 'lift_rating_unknown') {
    return (
      <div
        className="rounded-md border border-amber-500/60 bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-300 space-y-1"
        data-testid={`ranking-lift-${technicianId}`}
        data-lift-state="capacity_not_verified"
        data-lift-basis="lift_rating_unknown"
        role="note"
      >
        <p className="flex items-center gap-1 font-medium">
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden /> {CAPACITY_NOT_VERIFIED_TITLE}
          <span className="font-normal"> — {BASIS_TEXT.lift_rating_unknown}</span>
        </p>
        <p className="text-muted-foreground">
          Placement is allowed, but OptiBay has not verified that this vehicle is within the lift&apos;s capacity.
        </p>
      </div>
    );
  }
  if (ranking.liftVerificationRequired === true) {
    const basis = ranking.liftVerificationBasis ? BASIS_TEXT[ranking.liftVerificationBasis] : null;
    return (
      <div
        className="rounded-md border border-amber-500/60 bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-300 space-y-1"
        data-testid={`ranking-lift-${technicianId}`}
        data-lift-state="verification_required"
        data-lift-basis={ranking.liftVerificationBasis ?? 'unknown'}
        role="note"
      >
        <p className="flex items-center gap-1 font-medium">
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden /> {LIFT_VERIFICATION_LABEL}
          {basis ? <span className="font-normal"> — {basis}</span> : null}
        </p>
        {ranking.liftVerificationReason && <p>{ranking.liftVerificationReason}</p>}
        <p className="text-muted-foreground">
          Not shown as compatible: the vehicle cannot go on this technician&apos;s lift until a manager verifies the pairing.
        </p>
      </div>
    );
  }
  return null;
}
